import { useMemo, useState } from "react";
import { FiDownload, FiEye, FiX } from "react-icons/fi";
import * as XLSX from "xlsx";
import { useAppDispatch } from "../../store/hooks";
import { extractUnitsCases } from "../../store/superAdmin/unitsSlice";
import { useToast } from "../utilities/Toast";
import { CustomDatePicker } from "../common/DatePickers";

type Preset = "day" | "week" | "month" | "custom";

type ExtractRow = Record<string, unknown>;

const COLUMNS: { key: string; label: string }[] = [
  { key: "case_id", label: "Case ID" },
  { key: "allocated_by", label: "Allocated By" },
  { key: "designer", label: "Designer" },
  { key: "qc1", label: "QC1" },
  { key: "qc2", label: "QC2" },
  { key: "uploaded_by", label: "Uploaded By" },
  { key: "units_by", label: "Units By" },
  { key: "design_units", label: "Design Units" },
  { key: "delivery_units", label: "Delivery Units" },
  { key: "case_came_at", label: "Case Came At" },
  { key: "uploaded_at", label: "Uploaded At" },
  { key: "type", label: "Type" },
];

const pad = (n: number) => String(n).padStart(2, "0");

const toLocalDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const presetLabel: Record<Preset, string> = {
  day: "Day",
  week: "1 Week",
  month: "1 Month",
  custom: "Custom",
};

const UnitsExtractPanel = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const today = toLocalDate(new Date());

  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<Preset>("day");
  const [dayDate, setDayDate] = useState(today);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ExtractRow[] | null>(null);

  const range = useMemo(() => {
    if (preset === "day") return { from: dayDate, to: dayDate };
    if (preset === "week") {
      return { from: toLocalDate(addDays(new Date(), -6)), to: today };
    }
    if (preset === "month") {
      return { from: toLocalDate(addDays(new Date(), -29)), to: today };
    }
    return { from: fromDate, to: toDate };
  }, [preset, dayDate, fromDate, toDate, today]);

  const excelRows = (rows: ExtractRow[]) =>
    rows.map((row) =>
      Object.fromEntries(
        COLUMNS.map(({ key, label }) => [label, row[key] ?? ""]),
      ),
    );

  const downloadExcel = (rows: ExtractRow[]) => {
    const worksheet = XLSX.utils.json_to_sheet(excelRows(rows));
    worksheet["!cols"] = COLUMNS.map((col) => ({
      wch: Math.max(col.label.length + 2, 16),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cases");
    XLSX.writeFile(
      workbook,
      `Units_Cases_${range.from}_to_${range.to}.xlsx`,
    );
  };

  const loadRows = async () => {
    if (range.from > range.to) {
      showToast("From date cannot be after to date", "error");
      return null;
    }
    setLoading(true);
    try {
      const rows = await dispatch(
        extractUnitsCases({ from: range.from, to: range.to }),
      ).unwrap();
      if (!rows?.length) {
        showToast("No cases found for the selected period", "error");
        return [];
      }
      return rows;
    } catch (error) {
      console.error(error);
      showToast("Failed to extract cases", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleView = async () => {
    const rows = await loadRows();
    if (!rows?.length) return;
    setOpen(false);
    setPreview(rows);
  };

  const handleDownload = async () => {
    const rows = await loadRows();
    if (!rows?.length) return;
    downloadExcel(rows);
    showToast("Export downloaded successfully", "success");
  };

  const fieldClass =
    "rounded-lg border border-[#C9D4D6] bg-white px-2.5 py-1.5 text-xs text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition"
      >
        <FiDownload size={14} />
        Extract Excel
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-40 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-[#E8E8E4] bg-white p-4 text-[#4A4A48] shadow-xl">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold">Extract cases</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-[#8A8A85] hover:bg-[#F8F8F6] hover:text-[#4A4A48]"
              aria-label="Close extract"
            >
              <FiX size={16} />
            </button>
          </div>

          <div className="mb-3 grid grid-cols-4 gap-1 rounded-xl bg-[#F8F8F6] p-1">
            {(Object.keys(presetLabel) as Preset[]).map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setPreset(id)}
                className={`rounded-lg px-1.5 py-1.5 text-[11px] font-medium ${
                  preset === id
                    ? "bg-white text-[#2fa9bf] shadow-sm"
                    : "text-[#6B6B66] hover:text-[#4A4A48]"
                }`}
              >
                {presetLabel[id]}
              </button>
            ))}
          </div>

          {preset === "day" ? (
            <div className="mb-3">
              <CustomDatePicker label="Date" value={dayDate} onChange={setDayDate} />
            </div>
          ) : null}

          {preset === "custom" ? (
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <CustomDatePicker
                label="From"
                value={fromDate}
                max={toDate}
                onChange={setFromDate}
              />
              <CustomDatePicker
                label="To"
                value={toDate}
                min={fromDate}
                onChange={setToDate}
              />
            </div>
          ) : (
            <p className="mb-3 text-[11px] text-[#8A8A85]">
              {range.from === range.to
                ? `Cases from ${range.from}`
                : `Cases from ${range.from} to ${range.to}`}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleView}
              disabled={loading}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#C9D4D6] bg-white px-3 py-2 text-xs font-semibold text-[#4A4A48] hover:border-[#2fa9bf] hover:text-[#2fa9bf] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiEye size={14} />
              {loading ? "Loading…" : "View"}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#2fa9bf] px-3 py-2 text-xs font-semibold text-white hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiDownload size={14} />
              {loading ? "Exporting…" : "Download"}
            </button>
          </div>
        </div>
      ) : null}

      {preview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6">
          <div className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 bg-[#2fa9bf] px-5 py-4 text-white">
              <div>
                <h3 className="text-lg font-bold">Extracted cases</h3>
                <p className="mt-1 text-sm text-white/90">
                  {preview.length} case{preview.length === 1 ? "" : "s"} ·{" "}
                  {range.from === range.to
                    ? range.from
                    : `${range.from} to ${range.to}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadExcel(preview)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#2fa9bf] hover:bg-white/90"
                >
                  <FiDownload size={14} />
                  Download Excel
                </button>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="rounded-lg px-2 text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white"
                  aria-label="Close preview"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-full border-separate border-spacing-0 text-left text-[13px]">
                <thead className="sticky top-0 z-10 bg-[#FDFBF2]">
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="whitespace-nowrap border-b border-[#E8E8E4] px-3 py-2.5 font-semibold uppercase tracking-wide text-[#8A8A85]"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, index) => (
                    <tr
                      key={`${row.case_id}-${index}`}
                      className={index % 2 === 0 ? "bg-white" : "bg-[#F8F8F6]"}
                    >
                      {COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          className="whitespace-nowrap border-b border-[#F0F0EC] px-3 py-2 text-[#4A4A48]"
                        >
                          {String(row[col.key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UnitsExtractPanel;
